/**
 * Copyright Higharc Inc. 2019
 *
 * Dynamic sky material
 * @module
 */
import { BackSide, Color, DirectionalLight, ShaderMaterial, Vector3 } from "three";
import { setToneMappingDefine } from "../threejs/higharc/ToneMappingDefines";
import { TONE_MAPPING_ALGORITHM } from "../toneMappingConstants";
import { noIdMaterial } from "./idMaterial";

export interface DynamicSkyHemiLightParams {
  hemiLightFactor: number;
  hemiLightHorizontal: number;
  hemiLightTopColor: Color;
  hemiLightHorizonColor: Color;
  hemiLightBottomColor: Color;
  hemiLightIntensity: number;
}

export interface DynamicSkyParams extends DynamicSkyHemiLightParams {
  dirLight: DirectionalLight;
  upVector: Vector3;
  topColor: Color;
  topMagnitude: number;
  horizonColor: Color;
  horizonMagnitude: number;
  bottomColor: Color;
  bottomMagnitude: number;
  horizonSolidWidth: number;
  horizonGradientWidth: number;
  horizonInclination: number; // This is normalized between -1 and 1. A negative value moves the horizon down.
  sunCenterSize: number;
  sunCenterBlendAlpha: number;
  sunCenterColor: Color;
  sunCenterMagnitude: number;
  sunHaloSize: number;
  sunHaloBlendAlpha: number;
  sunHaloColor: Color;
  sunHaloMagnitude: number;
  sunHazeSize: number;
  sunHazeBlendAlpha: number;
  sunHazeColor: Color;
  sunHazeMagnitude: number;
  sunAlphaBlendingFactor: number; // Control weighting of additive and alpha blending.
}

const dynamicSkyShader = {
  vertex: `
varying vec3 vPos;

void main( ) {
  vPos = position.xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`,

  fragment: `

// This is a stylized sky shader that's mostly based on just mixing colors rather than proper atmospheric scattering.
// The only nod towards scattering is that a bulge of the horizon color is created near the sun.

// Note that this shader assumes that the up direction is positive z.

#include <tonemapping_pars_fragment>

uniform vec3 sunDirection;
uniform vec3 upVector;
uniform vec3 topColor;
uniform vec3 horizonColor;
uniform vec3 bottomColor;
uniform float horizonSolidWidth;
uniform float horizonGradientWidth;
uniform float horizonInclination;
uniform float sunCenterSize;
uniform float sunCenterBlendAlpha;
uniform vec3 sunCenterColor;
uniform float sunHaloSize;
uniform float sunHaloBlendAlpha;
uniform vec3 sunHaloColor;
uniform float sunHazeSize;
uniform float sunHazeBlendAlpha;
uniform vec3 sunHazeColor;
uniform float sunAlphaBlendingFactor;  // If this is 1, then the sun is alpha blended. If it is 0, then the sun is additive blended.

// For mixing in a fake hemi light. If hemiLightFactor is 0, then the sky is rendered normally. If it is 1, then
// the sky is entirely replaced by a gradient going from top to bottom.
uniform float hemiLightFactor;

// If hemiLightHorizontal is 1, then the gradient starts from the horizon below the sun angle. If it's 0, then the gradient goes from top down.
uniform float hemiLightHorizontal;

uniform vec3 hemiLightTopColor;
uniform vec3 hemiLightHorizonColor;
uniform vec3 hemiLightBottomColor;
uniform float hemiLightIntensity;

uniform bool ssfxToneMappingEnabled;

varying vec3 vPos;

void main( ) {
  vec3 rayDir = normalize(vPos);

  float rayDotUp = dot(rayDir, upVector);

  float rayDotSun = clamp(dot(sunDirection, rayDir), 0.0, 1.0 );

  // We ensure that the color at the horizon and at the top of the sky are always exactly the colors set from parameters.
  float rayFromHorizon = (rayDotUp - horizonInclination);
  float horizonDistance = max(clamp(rayFromHorizon / (1.0 - horizonInclination), 0.0, 1.0), clamp(-rayFromHorizon / (1.0 + horizonInclination), 0.0, 1.0));
  float horizonWeight = clamp(1.0 - (horizonDistance - horizonSolidWidth) / ((1.0 - horizonSolidWidth) * horizonGradientWidth), 0.0, 1.0);

  vec3 skyColor = mix(topColor, horizonColor, horizonWeight);
  vec3 groundColor = mix(bottomColor, horizonColor, horizonWeight);
  vec3 col = mix(groundColor, skyColor, rayDotUp > horizonInclination ? 1.0 : 0.0);

  // sunCenterSize is a multiplier for natural diameter of the sun which is about 0.5 degrees.
  float sunCenterRadius = sunCenterSize * (0.25 * 3.14159 / 180.0);
  float sunHaloW = 100.0/max(sunHaloSize, 0.01);
  float sunHazeW = 100.0/max(sunHazeSize, 0.01);

  float sunHazeAlpha = clamp(pow(rayDotSun, sunHazeW) * sunHazeBlendAlpha, 0.0, 1.0);
  float sunHaloAlpha = clamp(pow(rayDotSun, sunHaloW) * sunHaloBlendAlpha, 0.0, 1.0);
  float sunAlpha = (1.0 - smoothstep(sunCenterRadius, sunCenterRadius + 0.005, acos(rayDotSun))) * sunCenterBlendAlpha;
  col = col * (1.0 - sunHazeAlpha * sunAlphaBlendingFactor) + sunHazeColor * sunHazeAlpha;
  col = col * (1.0 - sunHaloAlpha * sunAlphaBlendingFactor) + sunHaloColor * sunHaloAlpha;
  col = col * (1.0 - sunAlpha * sunAlphaBlendingFactor) + sunCenterColor * sunAlpha;

  // sunDirectionHorizontal is the sun direction projected to the ground plane.
  vec3 sunDirectionHorizontal = sunDirection - dot(sunDirection, upVector) * upVector;

  // The hemi light is oriented somewhere between the up vector and the projected sun direction.
  float rayDotHemiLight = dot(normalize(mix(upVector, sunDirectionHorizontal, hemiLightHorizontal)), rayDir);

  // If we'd want to make the gradient linear relative to the angle, we could use this:
  /* rayDotHemiLight = 1.0 - acos(rayDotHemiLight) * (2.0 / 3.1415926); */

  vec3 hemiTopMixedColor = mix(hemiLightHorizonColor, hemiLightTopColor, clamp(rayDotHemiLight, 0.0, 1.0));
  vec3 hemiLightCol = mix(hemiTopMixedColor, hemiLightBottomColor, clamp(-rayDotHemiLight, 0.0, 1.0));

  col = mix(col, hemiLightCol * hemiLightIntensity, hemiLightFactor);

  gl_FragColor = vec4(col, 1.0);

  if (ssfxToneMappingEnabled) {
    #if defined(DM_LINEAR_TONE_MAPPING) && DM_LINEAR_TONE_MAPPING == 1
      gl_FragColor.rgb = LinearToneMapping( gl_FragColor.rgb );
    #elif defined(DM_REINHARD_TONE_MAPPING) && DM_REINHARD_TONE_MAPPING == 1
      gl_FragColor.rgb = ReinhardToneMapping( gl_FragColor.rgb );
    #elif defined(DM_CINEON_TONE_MAPPING) && DM_CINEON_TONE_MAPPING == 1
      gl_FragColor.rgb = OptimizedCineonToneMapping( gl_FragColor.rgb );
    #elif defined(DM_ACES_FILMIC_TONE_MAPPING) && DM_ACES_FILMIC_TONE_MAPPING == 1
      gl_FragColor.rgb = ACESFilmicToneMapping( gl_FragColor.rgb );
    #endif
  }

  gl_FragColor = linearToOutputTexel(gl_FragColor);
}
`,
};

const defaultParams: DynamicSkyParams = {
  dirLight: new DirectionalLight(0xffffff, 0.9),
  upVector: new Vector3(0, 0, 1),
  topColor: new Color(0.16, 0.48, 0.72),
  topMagnitude: 1.0,
  horizonColor: new Color(0.8, 0.64, 0.44),
  horizonMagnitude: 1.0,
  bottomColor: new Color(0.55, 0.67, 0.82),
  bottomMagnitude: 1.0,
  horizonSolidWidth: 0.1,
  horizonGradientWidth: 0.5,
  horizonInclination: 0.0,
  sunCenterSize: 2.0,
  sunCenterBlendAlpha: 0.6,
  sunCenterColor: new Color(1.0, 0.78, 0.6),
  sunCenterMagnitude: 1.0,
  sunHaloSize: 0.78,
  sunHaloBlendAlpha: 0.2,
  sunHaloColor: new Color(1.0, 0.5, 0.05),
  sunHaloMagnitude: 1.0,
  sunHazeSize: 25,
  sunHazeBlendAlpha: 0.11,
  sunHazeColor: new Color(1.0, 0.6, 0.2),
  sunHazeMagnitude: 1.0,
  sunAlphaBlendingFactor: 1.0,

  hemiLightFactor: 0.0,
  hemiLightHorizontal: 0.0,
  hemiLightTopColor: new Color(1.0, 1.0, 1.0),
  hemiLightHorizonColor: new Color(1.0, 1.0, 1.0),
  hemiLightBottomColor: new Color(1.0, 1.0, 1.0),
  hemiLightIntensity: 1.0,
};

export default function getDynamicSkyMaterial(userParams?: Partial<DynamicSkyParams>): ShaderMaterial {
  const finalParams = { ...defaultParams, ...userParams };

  const uniforms = {
    sunDirection: { value: new Vector3().copy(finalParams.dirLight.position).normalize() },
    upVector: { value: finalParams.upVector },
    topColor: { value: new Color().copySRGBToLinear(finalParams.topColor).multiplyScalar(finalParams.topMagnitude) },
    horizonColor: {
      value: new Color().copySRGBToLinear(finalParams.horizonColor).multiplyScalar(finalParams.horizonMagnitude),
    },
    bottomColor: {
      value: new Color().copySRGBToLinear(finalParams.bottomColor).multiplyScalar(finalParams.bottomMagnitude),
    },
    horizonInclination: { value: finalParams.horizonInclination },
    horizonSolidWidth: { value: finalParams.horizonSolidWidth },
    horizonGradientWidth: { value: finalParams.horizonGradientWidth },
    sunCenterSize: { value: finalParams.sunCenterSize },
    sunCenterBlendAlpha: { value: finalParams.sunCenterBlendAlpha },
    sunCenterColor: {
      value: new Color().copySRGBToLinear(finalParams.sunCenterColor).multiplyScalar(finalParams.sunCenterMagnitude),
    },
    sunHaloSize: { value: finalParams.sunHaloSize },
    sunHaloBlendAlpha: { value: finalParams.sunHaloBlendAlpha },
    sunHaloColor: {
      value: new Color().copySRGBToLinear(finalParams.sunHaloColor).multiplyScalar(finalParams.sunHaloMagnitude),
    },
    sunHazeSize: { value: finalParams.sunHazeSize },
    sunHazeBlendAlpha: { value: finalParams.sunHazeBlendAlpha },
    sunHazeColor: {
      value: new Color().copySRGBToLinear(finalParams.sunHazeColor).multiplyScalar(finalParams.sunHazeMagnitude),
    },
    sunAlphaBlendingFactor: { value: finalParams.sunAlphaBlendingFactor },

    hemiLightFactor: { value: finalParams.hemiLightFactor },
    hemiLightHorizontal: { value: finalParams.hemiLightHorizontal },
    hemiLightTopColor: { value: new Color().copySRGBToLinear(finalParams.hemiLightTopColor) },
    hemiLightHorizonColor: { value: new Color().copySRGBToLinear(finalParams.hemiLightHorizonColor) },
    hemiLightBottomColor: { value: new Color().copySRGBToLinear(finalParams.hemiLightBottomColor) },
    hemiLightIntensity: { value: finalParams.hemiLightIntensity },

    ssfxToneMappingEnabled: { value: true },
    toneMappingExposure: { value: 1.0 },
  };

  const defines = {};
  setToneMappingDefine(defines as any, TONE_MAPPING_ALGORITHM);

  const material = new ShaderMaterial({
    uniforms,
    defines,
    vertexShader: dynamicSkyShader.vertex,
    fragmentShader: dynamicSkyShader.fragment,
    side: BackSide,
    depthTest: false,
    depthWrite: false,
    name: "dynamicSkyMaterial",
  });

  material.userData.alternativeMaterial = noIdMaterial;

  return material;
}

/**
 * Note that this function doesn't set the "ssfxToneMappingEnabled" uniform, that's set dynamically from the render pipeline.
 */
export function assignSkyMaterialUniforms(material: ShaderMaterial, userParams: Partial<DynamicSkyParams>) {
  const finalParams = { ...defaultParams, ...userParams };
  const uniforms = material.uniforms;

  uniforms.sunDirection.value.copy(finalParams.dirLight.position).normalize();
  uniforms.upVector.value.copy(finalParams.upVector);
  uniforms.topColor.value.copy(finalParams.topColor).multiplyScalar(finalParams.topMagnitude);
  uniforms.horizonColor.value.copy(finalParams.horizonColor).multiplyScalar(finalParams.horizonMagnitude);
  uniforms.bottomColor.value.copy(finalParams.bottomColor).multiplyScalar(finalParams.bottomMagnitude);
  uniforms.horizonInclination.value = finalParams.horizonInclination;
  uniforms.horizonSolidWidth.value = finalParams.horizonSolidWidth;
  uniforms.horizonGradientWidth.value = finalParams.horizonGradientWidth;
  uniforms.sunCenterSize.value = finalParams.sunCenterSize;
  uniforms.sunCenterBlendAlpha.value = finalParams.sunCenterBlendAlpha;
  uniforms.sunCenterColor.value.copy(finalParams.sunCenterColor).multiplyScalar(finalParams.sunCenterMagnitude);
  uniforms.sunHaloSize.value = finalParams.sunHaloSize;
  uniforms.sunHaloBlendAlpha.value = finalParams.sunHaloBlendAlpha;
  uniforms.sunHaloColor.value.copy(finalParams.sunHaloColor).multiplyScalar(finalParams.sunHaloMagnitude);
  uniforms.sunHazeSize.value = finalParams.sunHazeSize;
  uniforms.sunHazeBlendAlpha.value = finalParams.sunHazeBlendAlpha;
  uniforms.sunHazeColor.value.copy(finalParams.sunHazeColor).multiplyScalar(finalParams.sunHazeMagnitude);
  uniforms.sunAlphaBlendingFactor.value = finalParams.sunAlphaBlendingFactor;

  uniforms.hemiLightFactor.value = finalParams.hemiLightFactor;
  uniforms.hemiLightHorizontal.value = finalParams.hemiLightHorizontal;
  uniforms.hemiLightTopColor.value.copy(finalParams.hemiLightTopColor);
  uniforms.hemiLightHorizonColor.value.copy(finalParams.hemiLightHorizonColor);
  uniforms.hemiLightBottomColor.value.copy(finalParams.hemiLightBottomColor);
  uniforms.hemiLightIntensity.value = finalParams.hemiLightIntensity;
}
